/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Injectable } from '@angular/core'
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs'
import { catchError, filter, first, map, shareReplay, tap } from 'rxjs/operators'
import { FALLBACK_COUNTRY_LIST, FALLBACK_EVENT_TYPES, FALLBACK_PLANS, FALLBACK_TIMEZONES } from 'src/environments/go-api-fallbacks'
import { environment } from 'src/environments/environment'

import { ApiService } from '@shared/services/api.service'
import { Timezone } from '@shared/models/timezone.model'
import { Plan } from '../models/plan.model'
import { EventType } from '../modules/notifications/shared/notification.model'
import { Language } from '../models/language.model'
import { mapProviders } from '@app/core/constants/map-providers'
import { mapApiOptions } from '@app/core/constants/map-api-options'

export enum MapProvider {
  Default = 'default',
  Google = 'google',
  Mapbox = 'mapbox',
}

export enum MapApiOption {
  SnapToRoad = 'snapToRoad',
  GeolocationForRoutes = 'geolocationForRoutes',
  GeolocationForFuelStations = 'geolocationForFuelStation',
}

@Injectable({ providedIn: 'root' })
export class DataService {
  private timezones$: Observable<Timezone[]>
  private plans$: Observable<Plan[]>

  private mapProvider = new BehaviorSubject<MapProvider>(MapProvider.Mapbox)
  readonly mapProvider$ = this.mapProvider.asObservable().pipe(filter((provider) => !!provider))

  private isGeolocationForRoutes = new BehaviorSubject<boolean | null>(null)
  readonly isGeolocationForRoutes$ = this.isGeolocationForRoutes.asObservable().pipe(filter((v): v is boolean => v !== null))

  private eventTypes = new BehaviorSubject<EventType[]>(null)
  readonly eventTypes$ = this.eventTypes.asObservable().pipe(filter(Boolean))

  constructor(private apiService: ApiService) {}

  populate() {
    const mapProvider$ = this.getMapProvider()
    const mapApiOptionGeolocationForRoutes$ = this.getMapApiOptionGeolocationForRoutes()
    const eventTypes$ = this.getEventTypes()

    return forkJoin([mapProvider$, mapApiOptionGeolocationForRoutes$, eventTypes$]).pipe(
      tap(([mapProvider, mapApiOptionGeolocationForRoutes, eventTypes]) => {
        this.setMapProvider(mapProvider)
        this.setIsGeolocationForRoutes(mapApiOptionGeolocationForRoutes)
        this.setEventTypes(eventTypes)
      }),
    )
  }

  getTimezones() {
    if (!this.timezones$) {
      this.timezones$ = this.apiService.get('/timezones').pipe(
        map((resp) => <Observable<Timezone[]>>resp['data']),
        catchError(() => of(FALLBACK_TIMEZONES as unknown as Timezone[])),
        shareReplay(),
        first(),
      ) as Observable<Timezone[]>
    }
    return this.timezones$
  }

  getCurrentEventTypes(): EventType[] {
    return this.eventTypes.getValue()
  }

  getEventTypes(): Observable<EventType[]> {
    if (environment.fakeApis) {
      return of(FALLBACK_EVENT_TYPES as unknown as EventType[])
    }
    return this.apiService
      .get('/notifications/event-types')
      .pipe(catchError(() => of(FALLBACK_EVENT_TYPES as unknown as EventType[]))) as Observable<EventType[]>
  }

  getPlans() {
    if (!this.plans$) {
      this.plans$ = this.apiService.get('/plans').pipe(
        map((resp) => <Observable<Plan[]>>resp['data']),
        catchError(() => of(FALLBACK_PLANS as unknown as Plan[])),
        shareReplay(),
        first(),
      ) as Observable<Plan[]>
    }
    return this.plans$
  }

  getMapProvider() {
    if (environment.fakeApis) {
      return of(MapProvider.Mapbox) as Observable<MapProvider>
    }
    const isApiProvider =
      (provider) =>
      ({ id }) =>
        id === provider.value
    return this.apiService.get('/settings/provider').pipe(
      map((resp) => resp['data']),
      map((provider: any) => (provider.value && mapProviders.find(isApiProvider(provider)).name) || MapProvider.Mapbox),
      catchError(() => of(MapProvider.Mapbox)),
    ) as Observable<MapProvider>
  }

  getMapApiOptionGeolocationForRoutes() {
    if (environment.fakeApis) {
      return of(false)
    }
    const isGeolocationForRoutes =
      (opts) =>
      ({ id, name }) =>
        opts.includes(id) && name === MapApiOption.GeolocationForRoutes
    return this.apiService.get(`/settings/mapApiOptions`).pipe(
      map((resp) => resp['data']),
      map((option: any) => option.value && !!mapApiOptions.find(isGeolocationForRoutes(option.value))),
      catchError(() => of(false)),
    )
  }

  getLanguages(): Observable<Language[]> {
    // PHP returns raw array; GO returns {data: [...]} envelope — handle both during migration
    return this.apiService
      .get<Language[] | { data: Language[] }>('/settings/language/list')
      .pipe(map((resp) => (Array.isArray(resp) ? resp : (resp?.data ?? []))))
  }

  getCountryList(): Observable<Record<string, string>> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.apiService.get('/country/list').pipe(
      map((response) => response['data']),
      catchError(() => of(FALLBACK_COUNTRY_LIST)),
    )
  }

  setMapProvider(provider: MapProvider) {
    this.mapProvider.next(provider)
  }

  setIsGeolocationForRoutes(isGeolocationForRoutes: boolean) {
    this.isGeolocationForRoutes.next(isGeolocationForRoutes)
  }

  setEventTypes(eventTypes: EventType[]) {
    this.eventTypes.next(eventTypes)
  }
}
